import type React from 'react';
import { useState, createContext, useContext, type PropsWithChildren } from 'react';

export interface BaseModalProps {
  onClose: () => void;
}

interface GlobalModalContextShape {
  showModal: <P extends BaseModalProps>(
    ModalComponent: React.VFC<Omit<P, keyof BaseModalProps> & BaseModalProps>,
    modalProps: Omit<P, keyof BaseModalProps>,
  ) => void;
  hideModal: () => void;
}

const GlobalModalContext = createContext<GlobalModalContextShape | undefined>(undefined);

export const useGlobalModalContext = (): GlobalModalContextShape => {
  const value = useContext(GlobalModalContext);
  if (value === undefined) throw new Error('useGlobalModalContext must be used within a GlobalModalProvider');
  return value;
};

export const GlobalModal: React.FC<PropsWithChildren> = ({ children }) => {
  const [element, setElement] = useState<JSX.Element | undefined>(undefined);

  const handleHideModal = () => {
    setElement(undefined);
  };

  const handleShowModal: GlobalModalContextShape['showModal'] = (Component, props) => {
    setElement(<Component data-cy="global-modal" {...props} onClose={handleHideModal} />);
  };

  return (
    <GlobalModalContext.Provider value={{ showModal: handleShowModal, hideModal: handleHideModal }}>
      {element}
      {children}
    </GlobalModalContext.Provider>
  );
};
